// ===============================
// FILE: src/api/adminComplianceApi.js
// ===============================
import { adminJson, adminApi } from "./adminHttp";

function enc(v) {
  return encodeURIComponent(String(v ?? ""));
}

export function getAdminMe() {
  try {
    const raw = localStorage.getItem("admin_me");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function withAdminId(path) {
  const me = getAdminMe();
  const adminId = me?.id;
  if (!adminId) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}adminId=${enc(adminId)}`;
}

/** Build API path (relative) for backend streaming controller */
export function buildVehicleFileViewPath(fileUrlOrKey) {
  if (!fileUrlOrKey) return "";
  return withAdminId(`/admin/vehicles/files/view?fileUrl=${enc(fileUrlOrKey)}`);
}

export function buildVehicleFileDownloadPath(fileUrlOrKey) {
  if (!fileUrlOrKey) return "";
  return withAdminId(`/admin/vehicles/files/download?fileUrl=${enc(fileUrlOrKey)}`);
}

function filenameFromContentDisposition(cd) {
  if (!cd) return "";
  const mStar = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (mStar && mStar[1]) {
    try {
      return decodeURIComponent(mStar[1].replace(/"/g, "").trim());
    } catch {
      return mStar[1].replace(/"/g, "").trim();
    }
  }
  const m = cd.match(/filename\s*=\s*"([^"]+)"/i) || cd.match(/filename\s*=\s*([^;]+)/i);
  return m && m[1] ? String(m[1]).replace(/"/g, "").trim() : "";
}

async function readErrorMessage(res) {
  try {
    const ct = (res.headers.get("content-type") || "").toLowerCase();
    if (ct.includes("application/json")) {
      const j = await res.json().catch(() => null);
      if (j?.message) return String(j.message);
      if (j?.error) return String(j.error);
      return JSON.stringify(j);
    }
  } catch {}
  const text = await res.text().catch(() => "");
  return text || `File request failed (${res.status})`;
}

async function fetchFileBlob(path) {
  if (!path) {
    const err = new Error("No file url found");
    err.status = 400;
    throw err;
  }

  const url = path + (path.includes("?") ? "&" : "?") + "_t=" + Date.now();
  const res = await adminApi(url, { method: "GET" });

  if (!res.ok) {
    const msg = await readErrorMessage(res);
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }

  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") || "";
  const filename = filenameFromContentDisposition(cd) || "file";
  return { blob, filename };
}

export async function openAdminVehicleFile(fileUrlOrKey) {
  const path = buildVehicleFileViewPath(fileUrlOrKey);
  const { blob } = await fetchFileBlob(path);

  const objUrl = URL.createObjectURL(blob);
  window.open(objUrl, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
}

export async function downloadAdminVehicleFile(fileUrlOrKey) {
  const path = buildVehicleFileDownloadPath(fileUrlOrKey);
  const { blob, filename } = await fetchFileBlob(path);

  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename || "file";
  document.body.appendChild(a);
  a.click();
  a.remove();

  setTimeout(() => URL.revokeObjectURL(objUrl), 10_000);
}

/* ---------------- Compliance APIs ---------------- */

export async function listComplianceQueue({ status, bucket, serviceType, q, page = 0, size = 25 } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (bucket) params.set("bucket", bucket);
  if (serviceType) params.set("serviceType", serviceType);
  if (q) params.set("q", q);
  params.set("page", String(page));
  params.set("size", String(size));

  const url = withAdminId(`/admin/compliance/queue?${params.toString()}`);
  return adminJson(url);
}

export async function listAllComplianceQueue({ status, bucket, serviceType, q } = {}) {
  const pageSize = 200;
  const first = await listComplianceQueue({ status, bucket, serviceType, q, page: 0, size: pageSize });
  const content = Array.isArray(first?.content) ? [...first.content] : [];
  const totalPages = Number(first?.totalPages ?? 0) || 0;

  for (let page = 1; page < totalPages; page += 1) {
    const next = await listComplianceQueue({ status, bucket, serviceType, q, page, size: pageSize });
    if (Array.isArray(next?.content)) {
      content.push(...next.content);
    }
  }

  return content;
}

export async function getComplianceDetail(regNo) {
  const url = withAdminId(`/admin/compliance/${enc(regNo)}`);
  return adminJson(url);
}

/**
 * approve = MARK REVIEWED (audit-only)
 */
export async function approveCompliance(regNo, { reason } = {}) {
  const url = withAdminId(`/admin/compliance/${enc(regNo)}/approve`);
  return adminJson(url, {
    method: "POST",
    body: JSON.stringify({ reason: reason || "" }),
  });
}

export async function cancelCompliance(regNo, { reason } = {}) {
  const url = withAdminId(`/admin/compliance/${enc(regNo)}/cancel`);
  return adminJson(url, {
    method: "POST",
    body: JSON.stringify({ reason: reason || "" }),
  });
}

export async function requestComplianceCorrection(regNo, payload) {
  const url = withAdminId(`/admin/compliance/${enc(regNo)}/request-correction`);
  return adminJson(url, {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });
}

export async function recomputeCompliance(regNo) {
  const url = withAdminId(`/admin/compliance/${enc(regNo)}/recompute`);
  return adminJson(url, { method: "POST" });
}

export async function overrideApproveCompliance(regNo, { reason } = {}) {
  const url = withAdminId(`/admin/compliance/${enc(regNo)}/override-approve`);
  return adminJson(url, {
    method: "POST",
    body: JSON.stringify({ reason: reason || "" }),
  });
}

export async function forceVerifyCompliance(regNo, { reason } = {}) {
  const url = withAdminId(`/admin/compliance/${enc(regNo)}/force-verify`);
  return adminJson(url, {
    method: "POST",
    body: JSON.stringify({ reason: reason || "" }),
  });
}

export async function unlockReuploadCompliance(regNo, payload) {
  const url = withAdminId(`/admin/compliance/${enc(regNo)}/unlock-reupload`);
  return adminJson(url, {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });
}
